import React, { useEffect, useMemo, useState } from 'react';

import { useGraphQLMutation } from '@betterment/js-runtime';

import { SignablePackageComponentProps } from './data';
import { DocusignSessionEndEventProps } from './Docusign';
import DownloadLink from './DownloadLink';
import { ForwardSignature } from './ForwardSignature';
import { CapturePackageSignatureDocument } from './graphql/mutation.capturePackageSignature.generated';
import { ReclaimPackageSignatureDocument } from './graphql/mutation.reclaimPackageSignature.generated';
import { useSignablePackageQuery } from './hooks';
import LoadingState from './LoadingState';
import PackageNotFoundError from './PackageNotFoundError';
import PackageStatus from './PackageStatus';
import { ReclaimSignature } from './ReclaimSignature';
import SigningInterface from './SigningInterface';

import { navigate } from '../../utils/pathHelpers';
import { urlBuilder } from '../../utils/urlBuilder';

const useReturnToPath = (defaultValue: string): string => {
  return useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('returnTo') ?? defaultValue;
  }, [defaultValue]);
};

const SignablePackage: React.FC<React.PropsWithChildren<SignablePackageComponentProps>> = ({ id }) => {
  const { isLoading, signablePackage: queriedPackage } = useSignablePackageQuery(id);
  const [errorMessage, setErrorMessage] = useState<string | null | undefined>(null);
  const [captureSignablePackage] = useGraphQLMutation(CapturePackageSignatureDocument);
  const [reclaimSignablePackage] = useGraphQLMutation(ReclaimPackageSignatureDocument);
  const [signablePackage, setSignablePackage] = useState(queriedPackage);
  const returnToPath = useReturnToPath(urlBuilder.dashboard());
  const [isReclaimingSignature, setIsReclaimingSignature] = useState(false);

  const onSessionEnd = (event: DocusignSessionEndEventProps) => {
    switch (event.sessionEndType) {
      case 'signing_complete':
        captureSignablePackage({ variables: { signablePackageId: id } })
          .then(response => {
            if (response.errors) {
              setErrorMessage(response.errors[0].message);
            } else if (response.data?.capturePackageSignature?.displayableErrors[0]) {
              setErrorMessage(response.data?.capturePackageSignature?.displayableErrors[0]?.message);
            } else if (response.data?.capturePackageSignature?.result?.signed) {
              navigate(urlBuilder.getSignablePackageSuccess(id, returnToPath));
            }
          })
          .catch(() => {
            setErrorMessage('Something went wrong');
          });
        break;
      case 'ttl_expired':
      case 'session_timeout':
        setErrorMessage('Session expired');
        break;
      default:
        setErrorMessage('Something went wrong');
    }
  };

  useEffect(() => setSignablePackage(queriedPackage), [queriedPackage]);

  const onReclaimSignature = () => {
    setIsReclaimingSignature(true);
    reclaimSignablePackage({ variables: { id } })
      .then(({ data, errors }) => {
        if (errors) {
          setErrorMessage(errors[0].message);
        } else if (data?.reclaimPackageSignature?.displayableErrors[0]) {
          setErrorMessage(data.reclaimPackageSignature.displayableErrors[0]?.message);
        } else if (data?.reclaimPackageSignature.result !== null) {
          setSignablePackage(data?.reclaimPackageSignature.result);
        }
        setIsReclaimingSignature(false);
      })
      .catch(() => {
        setErrorMessage('Something went wrong');
        setIsReclaimingSignature(false);
      });
  };

  const docusignSignature = signablePackage?.docusignSignature;
  const isReadyToBeSigned =
    docusignSignature && !signablePackage.signed && !errorMessage && !signablePackage.forwardedToEmail;

  return (
    <>
      {isLoading && <LoadingState />}
      {!isLoading && !signablePackage && <PackageNotFoundError />}
      {!isLoading && signablePackage && (
        <>
          <PackageStatus
            signablePackage={signablePackage}
            onReclaimSignature={onReclaimSignature}
            error={errorMessage}
          />
          {signablePackage.forwardedToEmail && (
            <ReclaimSignature loading={isReclaimingSignature} onReclaimSignature={onReclaimSignature} />
          )}
          {isReadyToBeSigned && !signablePackage.forwardedToEmail && (
            <ForwardSignature
              signablePackageId={id}
              onForwardingComplete={forwardedPackage => setSignablePackage(forwardedPackage)}
            />
          )}
          {isReadyToBeSigned && (
            <SigningInterface
              signablePackageId={id}
              docusignClientId={docusignSignature.clientId}
              docusignSignatureURL={docusignSignature.signatureUrl}
              onSessionEnd={onSessionEnd}
            />
          )}
          <DownloadLink packageId={signablePackage.id} isSigned={signablePackage.signed} />
        </>
      )}
    </>
  );
};

export default SignablePackage;
